// What a task's GitLab work looks like at a glance. Pure, so `gitlab.test.ts` can run it under Node.
import type { GitlabAttention } from "../api/types/GitlabAttention";
import type { GitlabRollup } from "../api/types/GitlabRollup";
import type { IconName } from "../components/Icon";

export type Translate = (key: string, options?: Record<string, unknown>) => string;
export type GitlabTone = "pass" | "fail" | "accent" | "neutral" | "stale";

const ATTENTION: Record<GitlabAttention, { icon: IconName; tone: GitlabTone }> = {
  failed_pipeline: { icon: "x", tone: "fail" },
  changed_since_tested: { icon: "stale", tone: "stale" },
  running_pipeline: { icon: "clock", tone: "accent" },
  draft: { icon: "edit", tone: "neutral" },
  open: { icon: "merge", tone: "accent" },
  merged: { icon: "check", tone: "pass" },
  deployed: { icon: "globe", tone: "pass" },
  branch_only: { icon: "branch", tone: "neutral" },
};

export type GitlabChipView = {
  state: GitlabAttention;
  icon: IconName;
  tone: GitlabTone;
  /** The short form on the card. */
  label: string;
  /** The tooltip and accessible label. */
  description: string;
  /** Changed since tested, when something more urgent leads the chip. */
  alsoChanged: boolean;
};

/** `development +1`: the first environment, and how many more. */
export function environmentsLabel(environments: string[]): string {
  const [first, ...rest] = environments;
  if (first === undefined) return "";
  return rest.length ? `${first} +${rest.length}` : first;
}

/**
 * The chip for a task's rollup; `null` draws nothing. The counts name MRs, and branches only when there are no MRs
 * or more branches than MRs: an MR and its source branch are one piece of work, not "1 MR, 1 branch".
 */
export function gitlabChip(rollup: GitlabRollup | null | undefined, t: Translate): GitlabChipView | null {
  if (!rollup?.state) return null;
  const state = rollup.state;
  const { mr_count: mrs, branch_count: branches, environments } = rollup;

  let label: string;
  if (state === "deployed" && environments.length > 0) label = environmentsLabel(environments);
  else if (state === "branch_only" && branches > 1) label = t("gitlab.chip.branch_onlyMany", { count: branches });
  else if (state !== "branch_only" && mrs > 1) label = t(`gitlab.chip.${state}Many`, { count: mrs });
  else label = t(`gitlab.chip.${state}`);

  const extraBranches = mrs > 0 ? branches - mrs : 0;
  const description = [
    t(`gitlab.chipHint.${state}`),
    mrs > 1 && t("gitlab.chipHint.mrs", { count: mrs }),
    mrs === 0 && branches > 1 && t("gitlab.chipHint.branches", { count: branches }),
    extraBranches > 0 && t("gitlab.chipHint.moreBranches", { count: extraBranches }),
    environments.length > 0 && t("gitlab.chipHint.environments", { environments: environments.join(", ") }),
    rollup.changed_since_tested && state !== "changed_since_tested" && t("gitlab.chipHint.changed_since_tested"),
    !rollup.connected && t("gitlab.chipHint.disconnected"),
  ]
    .filter(Boolean)
    .join(" ");

  return {
    state,
    ...ATTENTION[state],
    label,
    description: `GitLab: ${description}`,
    alsoChanged: rollup.changed_since_tested && state !== "changed_since_tested",
  };
}

const PIPELINE_WAITING = ["created", "waiting_for_resource", "preparing", "pending", "running"];
const PIPELINE_ENDED = ["success", "failed", "canceling", "canceled", "skipped", "manual", "scheduled"];

/** A pipeline's raw GitLab status as a chip. A status this app doesn't know reads as GitLab wrote it. */
export function pipelineChip(status: string, connected: boolean, t: Translate): { icon: IconName; tone: GitlabTone; label: string } {
  const known = PIPELINE_WAITING.includes(status) || PIPELINE_ENDED.includes(status);
  const word = known ? t(`gitlab.pipeline.${status}`) : status;
  if (!connected) return { icon: "clock", tone: "neutral", label: t("gitlab.pipeline.lastKnown", { status: word }) };
  if (status === "failed") return { icon: "x", tone: "fail", label: word };
  if (status === "success") return { icon: "check", tone: "pass", label: word };
  if (PIPELINE_WAITING.includes(status)) return { icon: "clock", tone: "accent", label: word };
  return { icon: "circle", tone: "neutral", label: word };
}

/** The last part of `group/sub/admin-panel`, for lines that name a repo in passing. */
export function shortRepo(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** The Code tab shows when the project has a repo or the task has links, and the regression suite can't link. */
export function hasCodeTab(projectEnabled: boolean, rollup: GitlabRollup | null | undefined, regressionSuite: boolean): boolean {
  return !regressionSuite && (projectEnabled || !!rollup);
}
